import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type ModalEntry, type ModalRoot, type ModalState } from './modal.types';

const initialState: ModalState = { stack: [] };

/**
 * The open-modal stack (W2).
 *
 * A stack rather than a single `{ type, data }` because nesting is a real need
 * here — a role form opens a permission picker, a verification case opens a
 * media viewer — and because the array ORDER is exactly the dialog-stack depth
 * `DialogStack` needs. Deriving depth from position means nothing has to
 * register itself on mount, which is where the classic bug lives: a dialog that
 * registers in its component body counts even when it never mounts, and the
 * parent recedes for nothing.
 */
const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    modalOpened(state, action: PayloadAction<ModalEntry>) {
      const existing = state.stack.findIndex((entry) => entry.type === action.payload.type);

      if (existing === -1) {
        state.stack.push(action.payload);
        return;
      }

      // Re-opening what is already open updates it in place. A double-click on
      // "Edit" must never produce two copies of the same dialog.
      state.stack[existing] = action.payload;
    },

    /** Closes a named modal, or the top one when called with nothing (Escape). */
    modalClosed: {
      reducer(state, action: PayloadAction<string | undefined>) {
        if (action.payload === undefined) {
          state.stack.pop();
          return;
        }
        state.stack = state.stack.filter((entry) => entry.type !== action.payload);
      },
      prepare: (type?: string) => ({ payload: type }),
    },

    allModalsClosed(state) {
      state.stack = [];
    },
  },
});

export const { modalOpened, modalClosed, allModalsClosed } = modalSlice.actions;
export const modalReducer = modalSlice.reducer;

export const selectModalStack = (state: ModalRoot): ModalEntry[] => state.modal.stack;

export const selectIsModalOpen =
  (type: string) =>
  (state: ModalRoot): boolean =>
    state.modal.stack.some((entry) => entry.type === type);

export const selectModalData =
  (type: string) =>
  (state: ModalRoot): ModalEntry['data'] =>
    state.modal.stack.find((entry) => entry.type === type)?.data;
